/**
 * The nine lifecycle stages (PRD §5.3).
 *
 * Separate from `StageBadge.tsx` so that file exports only a component — a
 * module mixing components and constants defeats fast refresh, and the rule
 * that says so is worth keeping honest.
 */
export const STAGES = [
  'SCOUTED',
  'SCREENED',
  'QUALIFIED',
  'PILOT_SCOPING',
  'PILOT_LIVE',
  'VALIDATED',
  'COMMERCIAL_ROLLOUT',
  'PARKED',
  'REJECTED',
] as const;

export type Stage = (typeof STAGES)[number];

/**
 * Dot colour per stage.
 *
 * The seven active stages walk the green → navy ramp in funnel order; parked
 * and rejected sit outside it, because they are not positions in the funnel.
 */
export const STAGE_DOT: Record<Stage, string> = {
  SCOUTED: 'bg-stage-1',
  SCREENED: 'bg-stage-2',
  QUALIFIED: 'bg-stage-3',
  PILOT_SCOPING: 'bg-stage-4',
  PILOT_LIVE: 'bg-stage-5',
  VALIDATED: 'bg-stage-6',
  COMMERCIAL_ROLLOUT: 'bg-stage-7',
  PARKED: 'bg-stage-parked',
  REJECTED: 'bg-stage-rejected',
};

/**
 * The seven funnel stages' colours, as CSS values, in funnel order.
 *
 * `STAGE_DOT` gives Tailwind classes, which is what a badge needs. This gives
 * raw colour values, which is what an inline `style` needs — the login ramp and
 * the funnel chart size their segments dynamically, and a class cannot carry a
 * computed width alongside a colour.
 *
 * Parked and rejected are excluded: this is the ramp *through* the funnel, and
 * neither is a position in it.
 */
export const STAGE_RAMP: readonly string[] = [
  'var(--stage-1)',
  'var(--stage-2)',
  'var(--stage-3)',
  'var(--stage-4)',
  'var(--stage-5)',
  'var(--stage-6)',
  'var(--stage-7)',
];

/**
 * The stage's colour as a CSS value, for a border or a bar.
 *
 * `STAGE_DOT` gives a `bg-*` class, which cannot be reused as a border colour,
 * and `STAGE_RAMP` covers only the seven funnel stages. This covers all nine,
 * so a parked or rejected column is coloured by the same lookup rather than by
 * a special case at the call site.
 */
export const STAGE_COLOR: Record<Stage, string> = {
  SCOUTED: 'var(--stage-1)',
  SCREENED: 'var(--stage-2)',
  QUALIFIED: 'var(--stage-3)',
  PILOT_SCOPING: 'var(--stage-4)',
  PILOT_LIVE: 'var(--stage-5)',
  VALIDATED: 'var(--stage-6)',
  COMMERCIAL_ROLLOUT: 'var(--stage-7)',
  PARKED: 'var(--stage-parked)',
  REJECTED: 'var(--stage-rejected)',
};
